/* global $, APP, interfaceConfig */
import Logger from 'meet-hour-logger';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { I18nextProvider } from 'react-i18next';
import { Provider } from 'react-redux';

import UIUtil from '../../../modules/UI/util/UIUtil';
import { createDeferred } from '../../../modules/util/helpers';
import UIEvents from '../../../service/UI/UIEvents';
import {
    createScreenSharingIssueEvent,
    sendAnalytics
} from '../analytics';
import { Avatar } from '../base/avatar';
import { i18next } from '../base/i18n';
import { MHParticipantConnectionStatus } from '../base/lib-meet-hour';
import { MEDIA_TYPE, VIDEO_TYPE } from '../base/media';
import { getScreenShareParticipantID } from '../base/mtApi';
import { getParticipantById } from '../base/participants';
import { getTrackByMediaTypeAndParticipant } from '../base/tracks';
import { CHAT_SIZE } from '../chat';
import { getNotesPaneOpen } from '../notes/functions';
import { getParticipantsPaneOpen } from '../participants-pane/functions';
import { PresenceLabel } from '../presence-status';
import { shouldDisplayTileView } from '../video-layout';

import { VIDEO_CONTAINER_TYPE, VideoContainer } from './VideoContainer.web';
import { updateKnownLargeVideoResolution } from './actions';

const logger = Logger.getLogger(__filename);

const DESKTOP_CONTAINER_TYPE = 'desktop';
const _reactRootsByContainer = new WeakMap();

/**
 * Returns an existing React root for the given container, or creates one.
 *
 * @param {HTMLElement} container - The target root container.
 * @returns {Object}
 */
function _getOrCreateRoot(container) {
    let root = _reactRootsByContainer.get(container);

    if (!root) {
        root = createRoot(container);
        _reactRootsByContainer.set(container, root);
    }

    return root;
}

/**
 * Unmounts and removes a cached root for the given container.
 *
 * @param {HTMLElement} container - The target root container.
 * @returns {void}
 */
function _disposeRoot(container) {
    if (!container) {
        return;
    }

    const root = _reactRootsByContainer.get(container);

    if (root) {
        root.unmount();
        _reactRootsByContainer.delete(container);
    }
}

/**
 * Updates the audio level indicator shadow of a large video element.
 *
 * @param {string} elementId - Target DOM element id.
 * @param {number|string} audioLevel - Audio level from 0 to 1.
 * @returns {void}
 */
function updateLargeVideoAudioLevel(elementId, audioLevel) {
    const element = document.getElementById(elementId);

    // Early return if element doesn't exist or isn't visible
    if (!element || !UIUtil.isVisible(element)) {
        return;
    }

    let level = parseFloat(audioLevel);

    level = isNaN(level) ? 0 : level;

    const shadowElements = element.getElementsByClassName('dynamic-shadow');

    if (!shadowElements || shadowElements.length === 0) {
        return;
    }

    const shadowElement = shadowElements[0];

    if (shadowElement && shadowElement.style) {
        const scale = 2;
        const primaryColor = (typeof interfaceConfig !== 'undefined' && interfaceConfig.AUDIO_LEVEL_PRIMARY_COLOR)
            || 'rgba(255,255,255,0.4)';
        const secondaryColor = (typeof interfaceConfig !== 'undefined' && interfaceConfig.AUDIO_LEVEL_SECONDARY_COLOR)
            || 'rgba(255,255,255,0.2)';

        const intLevel = level > 0.15 ? 20 : 0;
        const extLevel = parseFloat(((intLevel * scale * level) + intLevel).toFixed(0));

        const intBlur = intLevel ? 2 : 0;
        const extBlur = extLevel ? 6 : 0;

        shadowElement.style.boxShadow = [
            `0 0 ${intBlur}px ${intLevel}px ${primaryColor}`,
            `0 0 ${extBlur}px ${extLevel}px ${secondaryColor}`
        ].join(', ');
    }
}

/**
 * Manager for all Large containers.
 */
export default class LargeVideoManager {
    /**
     * Checks whether given container is a {@link VIDEO_CONTAINER_TYPE}.
     *
     * @param {string} containerType
     * @returns {boolean}
     */
    static isVideoContainer(containerType) {
        return (
            containerType === VIDEO_CONTAINER_TYPE
            || containerType === DESKTOP_CONTAINER_TYPE
        );
    }

    /**
     *
     */
    constructor(emitter) {
        /**
         * The map of LargeContainers where the key is the video container type.
         *
         * @type {Object.<string, Object>}
         */
        this.containers = {};
        this.eventEmitter = emitter;

        this.state = VIDEO_CONTAINER_TYPE;

        this.videoContainer = new VideoContainer(
            () => this.resizeContainer(VIDEO_CONTAINER_TYPE),
            emitter
        );
        this.addContainer(VIDEO_CONTAINER_TYPE, this.videoContainer);

        // use the same video container to handle desktop tracks
        this.addContainer(DESKTOP_CONTAINER_TYPE, this.videoContainer);

        /**
         * The preferred width passed as an argument to {@link updateContainerSize}.
         *
         * @type {number|undefined}
         */
        this.preferredWidth = undefined;

        /**
         * The preferred height passed as an argument to {@link updateContainerSize}.
         *
         * @type {number|undefined}
         */
        this.preferredHeight = undefined;

        /**
         * The calculated width that will be used for the large video.
         *
         * @type {number}
         */
        this.width = 0;

        /**
         * The calculated height that will be used for the large video.
         *
         * @type {number}
         */
        this.height = 0;

        /**
         * Cache the aspect ratio of the video displayed to detect changes to
         * the aspect ratio on video resize events.
         *
         * @type {number}
         */
        this._videoAspectRatio = 0;

        this.$container = $('#largeVideoContainer');

        this.$container.hover(
            e => this.onHoverIn(e),
            e => this.onHoverOut(e)
        );

        // Bind event handler so it is only bound once for every instance.
        this._onVideoResolutionUpdate
            = this._onVideoResolutionUpdate.bind(this);

        this.videoContainer.addResizeListener(this._onVideoResolutionUpdate);

        this._dominantSpeakerAvatarContainer = document.getElementById(
            'dominantSpeakerAvatarContainer'
        );

        this.avatarRoot = null;
    }

    /**
     * Removes any listeners registered on child components, including React roots.
     *
     * @returns {void}
     */
    destroy() {
        this.videoContainer.removeResizeListener(this._onVideoResolutionUpdate);

        this.removePresenceLabel();

        _disposeRoot(this._dominantSpeakerAvatarContainer);
        this.avatarRoot = null;

        if (this.presenceRoot) {
            this.presenceRoot.unmount();
            this.presenceRoot = null;
        }

        this.videoContainer.destroy();
    }

    /**
     *
     */
    onHoverIn(e) {
        if (!this.state) {
            return;
        }
        const container = this.getCurrentContainer();

        container?.onHoverIn?.(e);
    }

    /**
     *
     */
    onHoverOut(e) {
        if (!this.state) {
            return;
        }
        const container = this.getCurrentContainer();

        container?.onHoverOut?.(e);
    }

    /**
     *
     */
    get id() {
        const container = this.getCurrentContainer();

        // If a user switch for large video is in progress then provide what
        // will be the end result of the update.
        if (
            this.updateInProcess
            && this.newStreamData
            && this.newStreamData.id !== container?.id
        ) {
            return this.newStreamData.id;
        }

        return container?.id;
    }

    /**
     *
     */
    scheduleLargeVideoUpdate() {
        if (this.updateInProcess || !this.newStreamData) {
            return;
        }

        this.updateInProcess = true;

        // Include hide()/fadeOut only if we're switching between users
        const container = this.getCurrentContainer();
        const isUserSwitch = container?.id !== this.newStreamData.id
            || container?.stream?.getSourceName?.() !== this.newStreamData.stream?.getSourceName?.();
        const preUpdate = isUserSwitch && container?.hide ? container.hide() : Promise.resolve();

        preUpdate
            .then(() => {
                const { id, stream, videoType, resolve } = this.newStreamData;

                const isVideoContainer
                    = LargeVideoManager.isVideoContainer(videoType);

                this.newStreamData = null;

                this.state = videoType;
                const activeContainer = this.getCurrentContainer();

                // CONFERENCE_WILL_LEAVE -> destroy() sets #largeVideoContainer to
                // display:none. Visitor re-init must unhide it or stage/vertical
                // show an empty (or no) stage even after the track is attached.
                if (this.$container && this.$container.css('display') === 'none') {
                    this.$container.css({ display: 'inline-block' });
                }

                activeContainer?.setStream?.(id, stream, videoType);

                // change the avatar url on large
                this.updateAvatar();

                const isVideoMuted = !stream || stream.isMuted();
                const state = APP.store.getState();
                const participant = getParticipantById(state, id);

                // Default to ACTIVE when the backend has not reported a status.
                // Visitor-relayed participants often arrive without a
                // connectionStatus event, which would otherwise hide their
                // video behind an avatar.
                const connectionStatus = participant?.connectionStatus
                    || MHParticipantConnectionStatus.ACTIVE;
                const isFakeScreenshare = participant?.isFakeParticipant === 'LocalScreenShare'
                    || participant?.isFakeParticipant === 'RemoteScreenShare';
                const getSCParticipantId = isFakeScreenshare ? getScreenShareParticipantID(id) : id;
                const track = getTrackByMediaTypeAndParticipant(
                    state['features/base/tracks'],
                    isFakeScreenshare ? MEDIA_TYPE.SCREENSHARE : MEDIA_TYPE.VIDEO,
                    id
                );
                const isTrackMuted = !track || track.muted;

                const isVideoRenderable
                    = track && !(isVideoMuted && (track && track?.muted))
                    && (APP.conference.isLocalId(getSCParticipantId)
                        || connectionStatus
                            === MHParticipantConnectionStatus.ACTIVE || isFakeScreenshare);
                const isAudioOnly = APP.conference.isAudioOnly();
                const showAvatar
                    = isVideoContainer
                    && ((isAudioOnly && videoType !== VIDEO_TYPE.DESKTOP)
                        || !isVideoRenderable);

                let promise;

                if (
                    this.containers[this.state]?.videoType === 'desktop' && !isTrackMuted
                    && window.location === window.parent.location
                ) {
                    if (document.querySelector('.leftwatermark')) {
                        document.querySelector(
                            '.leftwatermark'
                        ).style.zIndex = 1;
                    }
                } else if (window.location === window.parent.location) {
                    if (document.querySelector('.leftwatermark')) {
                        document.querySelector('.leftwatermark').style.zIndex
                            = '';
                    }
                }

                logger.log('screensharing on large video - remote', getSCParticipantId, this.containers[this.state], track, APP.conference.isLocalId(getSCParticipantId));

                // do not show stream if video is muted
                // but we still should show watermark
                if (
                    showAvatar
                    || (this.containers[this.state]?.videoType === 'desktop'
                        && APP.conference.isLocalId(getSCParticipantId))
                ) {
                    this.showWatermark(true);

                    // If the intention of this switch is to show the avatar
                    // we need to make sure that the video is hidden
                    promise = activeContainer?.hide?.() || Promise.resolve();

                    if (
                        (!shouldDisplayTileView(state)
                            || participant?.pinned)
                        && participant
                        && !participant.local
                        && !(participant.isFakeParticipant && !isFakeScreenshare)
                    ) {
                        // remote participant only
                        const isScreenSharing = track?.videoType === 'desktop' && !isTrackMuted;

                        if (isScreenSharing) {
                            sendAnalytics(
                                createScreenSharingIssueEvent({
                                    source: 'large-video',
                                    connectionStatus,
                                    isVideoMuted,
                                    isAudioOnly,
                                    isVideoContainer,
                                    videoType
                                })
                            );
                        }
                    }
                } else if (this.containers[this.state]?.videoType === 'desktop'
                        && !APP.conference.isLocalId(getSCParticipantId)
                ) {
                    this.showWatermark(false);
                    promise = activeContainer?.show?.() || Promise.resolve();

                    // Make sure to force video display for screenshare
                    activeContainer?.showAvatar?.(false);
                } else {
                    promise = activeContainer?.show?.() || Promise.resolve();
                }

                // show the avatar on large if needed
                activeContainer?.showAvatar?.(showAvatar);

                // Clean up audio level after previous speaker.
                if (showAvatar) {
                    this.updateLargeVideoAudioLevel(0);
                }

                const messageKey
                    = connectionStatus === MHParticipantConnectionStatus.INACTIVE
                        ? 'connection.LOW_BANDWIDTH'
                        : '';

                // Do not show connection status message in the audio only mode,
                // because it's based on the video playback status.
                const overrideAndHide = APP.conference.isAudioOnly();

                this.updateParticipantConnStatusIndication(
                    id,
                    !overrideAndHide && messageKey
                );

                // Change the participant id the presence label is listening to.
                this.updatePresenceLabel(id);

                this.videoContainer.positionRemoteStatusMessages();

                if (
                    this.containers[this.state]?.videoType === 'desktop' && track && !track.muted
                    && APP.conference.isLocalId(getSCParticipantId)
                ) {
                    activeContainer?.showAvatar?.(true);
                    this.updateParticipantConnStatusIndication(
                        id,
                        'dialog.screenSharingUser'
                    );
                    this.updatePresenceLabel(id);
                    this.videoContainer.positionRemoteStatusMessages();
                }

                // resolve updateLargeVideo promise after everything is done
                promise.then(resolve);

                return promise;
            })
            .then(() => {
                // after everything is done check again if there are any pending new streams.
                this.updateInProcess = false;
                this.eventEmitter.emit(
                    UIEvents.LARGE_VIDEO_ID_CHANGED,
                    this.id
                );
                this.scheduleLargeVideoUpdate();
            });
    }

    /**
     * Shows/hides notification about participant's connectivity issues to be
     * shown on the large video area.
     *
     * @param {string} id - The id of remote participant(MUC nickname).
     * @param {string|null} messageKey - The i18n key of the message which will be
     * displayed on the large video or <tt>null</tt> to hide it.
     *
     * @private
     */
    updateParticipantConnStatusIndication(id, messageKey) {
        if (messageKey) {
            // Get user's display name
            const displayName = APP.conference.getParticipantDisplayName(id);

            this._setRemoteConnectionMessage(messageKey, { displayName });

            // Show it now only if the VideoContainer is on top
            this.showRemoteConnectionMessage(
                LargeVideoManager.isVideoContainer(this.state)
            );
        } else {
            // Hide the message
            this.showRemoteConnectionMessage(false);
        }
    }

    /**
     * Update large video.
     * Switches to large video even if previously other container was visible.
     *
     * @param {string} userID - The userID of the participant associated with the stream.
     * @param {Object?} stream - New stream.
     * @param {string?} videoType - New video type.
     * @returns {Promise}
     */
    updateLargeVideo(userID, stream, videoType) {
        if (this.newStreamData) {
            this.newStreamData.reject();
        }

        this.newStreamData = createDeferred();
        this.newStreamData.id = userID;
        this.newStreamData.stream = stream;
        this.newStreamData.videoType = videoType;

        this.scheduleLargeVideoUpdate();

        return this.newStreamData.promise;
    }

    /**
     * Update container size.
     *
     * @param {number} [width] - Desired width.
     * @param {number} [height] - Desired height.
     */
    updateContainerSize(width, height) {
        if (typeof width === 'number') {
            this.preferredWidth = width;
        }
        if (typeof height === 'number') {
            this.preferredHeight = height;
        }

        let widthToUse = this.preferredWidth || window.innerWidth;
        const state = APP.store.getState();
        const { isOpen } = state['features/chat'];
        const isParticipantsPaneOpen = getParticipantsPaneOpen(state);
        const isNotesPaneOpen = getNotesPaneOpen(state);

        if (isParticipantsPaneOpen || isNotesPaneOpen) {
            widthToUse -= 315;
        }

        if (isOpen && window.innerWidth > 580) {
            widthToUse -= CHAT_SIZE;
        }

        this.width = widthToUse;
        this.height = this.preferredHeight || window.innerHeight;
    }

    /**
     * Resize Large container of specified type.
     *
     * @param {string} type - Type of container which should be resized.
     * @param {boolean} [animate=false] - If resize process should be animated.
     */
    resizeContainer(type, animate = false) {
        const container = this.getContainer(type);

        container?.resize?.(this.width, this.height, animate);
    }

    /**
     * Resize all Large containers.
     *
     * @param {boolean} animate - If resize process should be animated.
     */
    resize(animate) {
        // resize all containers
        Object.keys(this.containers).forEach(type =>
            this.resizeContainer(type, animate)
        );
    }

    /**
     * Updates the src of the dominant speaker avatar.
     */
    updateAvatar() {
        if (!this._dominantSpeakerAvatarContainer) {
            return;
        }

        if (!this.avatarRoot) {
            this.avatarRoot = _getOrCreateRoot(this._dominantSpeakerAvatarContainer);
        }

        this.avatarRoot.render(
            <Provider store = { APP.store }>
                <Avatar
                    id = 'dominantSpeakerAvatar'
                    participantId = { this.id } />
            </Provider>
        );
    }

    /**
     * Updates the audio level indicator of the large video.
     *
     * @param {number} lvl - The new audio level to set.
     */
    updateLargeVideoAudioLevel(lvl) {
        updateLargeVideoAudioLevel('dominantSpeaker', lvl);
    }

    /**
     * Displays a message of the passed in participant id's presence status. The
     * message will not display if the remote connection message is displayed.
     *
     * @param {string} id - The participant ID whose associated user's presence
     * status should be displayed.
     * @returns {void}
     */
    updatePresenceLabel(id) {
        const presenceLabelContainer = document.getElementById('remotePresenceMessage');

        if (!presenceLabelContainer) {
            return;
        }

        if (!this.presenceRoot) {
            this.presenceRoot = _getOrCreateRoot(presenceLabelContainer);
        }

        this.presenceRoot.render(
            <Provider store = { APP.store }>
                <I18nextProvider i18n = { i18next }>
                    <PresenceLabel
                        className = 'presence-label'
                        participantID = { id } />
                </I18nextProvider>
            </Provider>
        );
    }

    /**
     * Removes the messages about the displayed participant's presence status.
     *
     * @returns {void}
     */
    removePresenceLabel() {
        const presenceLabelContainer = document.getElementById('remotePresenceMessage');

        _disposeRoot(presenceLabelContainer);
        this.presenceRoot = null;
    }

    /**
     * Show or hide watermark.
     *
     * @param {boolean} show
     */
    showWatermark(show) {
        $('.watermark').css('visibility', show ? 'visible' : 'hidden');
    }

    /**
     * Shows/hides the "avatar" message which is to be displayed either in
     * the middle of the screen or below the avatar image.
     *
     * @param {boolean|undefined} [show=undefined] - <tt>true</tt> to show
     * the avatar message or <tt>false</tt> to hide it.
     */
    showRemoteConnectionMessage(show) {
        let shouldShow = show;

        if (typeof shouldShow !== 'boolean') {
            const participant = getParticipantById(
                APP.store.getState(),
                this.id
            );
            const connStatus = participant?.connectionStatus;

            shouldShow
                = !APP.conference.isLocalId(this.id)
                && (connStatus === MHParticipantConnectionStatus.INTERRUPTED
                    || connStatus === MHParticipantConnectionStatus.INACTIVE);
        }

        if (shouldShow) {
            $('#remoteConnectionMessage').css({ display: 'block' });
        } else {
            $('#remoteConnectionMessage').hide();
        }
    }

    /**
     * Updates the text which describes that the remote user is having
     * connectivity issues.
     *
     * @param {string} msgKey - The translation key which will be used to get
     * the message text.
     * @param {Object} msgOptions - Translation options object.
     *
     * @private
     */
    _setRemoteConnectionMessage(msgKey, msgOptions) {
        if (msgKey) {
            $('#remoteConnectionMessage')
                .attr('data-i18n', msgKey)
                .attr('data-i18n-options', JSON.stringify(msgOptions));
            APP.translation.translateElement(
                $('#remoteConnectionMessage'),
                msgOptions
            );
        }
    }

    /**
     * Add container of specified type.
     *
     * @param {string} type - Container type.
     * @param {Object} container - Container to add.
     */
    addContainer(type, container) {
        if (this.containers[type]) {
            throw new Error(`container of type ${type} already exist`);
        }

        this.containers[type] = container;
        this.resizeContainer(type);
    }

    /**
     * Get Large container of specified type.
     *
     * @param {string} type - Container type.
     * @returns {Object}
     */
    getContainer(type) {
        const container = this.containers[type];

        if (!container) {
            throw new Error(`container of type ${type} doesn't exist`);
        }

        return container;
    }

    /**
     * Returns LargeContainer for the current state.
     *
     * @returns {Object}
     */
    getCurrentContainer() {
        return this.getContainer(this.state);
    }

    /**
     * Returns type of the current LargeContainer.
     *
     * @returns {string}
     */
    getCurrentContainerType() {
        return this.state;
    }

    /**
     * Remove Large container of specified type.
     *
     * @param {string} type - Container type.
     */
    removeContainer(type) {
        if (!this.containers[type]) {
            throw new Error(`container of type ${type} doesn't exist`);
        }

        delete this.containers[type];
    }

    /**
     * Show Large container of specified type.
     * Does nothing if such container is already visible.
     *
     * @param {string} type - Container type.
     * @returns {Promise}
     */
    showContainer(type) {
        if (this.state === type) {
            return Promise.resolve();
        }

        const oldContainer = this.containers[this.state];

        if (LargeVideoManager.isVideoContainer(this.state)) {
            this.showWatermark(false);
            this.showRemoteConnectionMessage(false);
        }
        oldContainer?.hide?.();

        this.state = type;
        const container = this.getContainer(type);

        return (container?.show?.() || Promise.resolve()).then(() => {
            if (LargeVideoManager.isVideoContainer(type)) {
                this.showWatermark(true);
                this.showRemoteConnectionMessage();
            }
        });
    }

    /**
     * Changes the flipX state of the local video.
     *
     * @param {boolean} val - True if flipped.
     */
    onLocalFlipXChange(val) {
        this.videoContainer.setLocalFlipX(val);
    }

    /**
     * Dispatches an action to update the known resolution state of the large video and adjusts container sizes when the
     * resolution changes.
     *
     * @private
     * @returns {void}
     */
    _onVideoResolutionUpdate() {
        const { height, width } = this.videoContainer.getStreamSize();
        const { resolution } = APP.store.getState()['features/large-video'];

        // "480p", "720p" and the rest name the *short* side of the frame, and that is what the quality label
        // buckets against. Reporting the raw videoHeight read the long side for portrait senders - a phone
        // sending 360x640 reported 640 and was labelled SD when the stream it is actually carrying is 360.
        // Landscape is unaffected: there the short side already is the height.
        const streamResolution = width > 0 && height > 0 ? Math.min(width, height) : height;

        if (streamResolution !== resolution) {
            APP.store.dispatch(updateKnownLargeVideoResolution(streamResolution));
        }

        const currentAspectRatio = height === 0 ? 0 : width / height;

        if (this._videoAspectRatio !== currentAspectRatio) {
            this._videoAspectRatio = currentAspectRatio;
            this.resize();
        }
    }
}
